export const HOME_CATEGORY_LABEL = "Latest News";

const HOME_LABELS_BY_DOMAIN: Record<string, string> = {
  "jejuqq.com": "最新消息",
  "jejujapan.com": "最新ニュース",
  "voicejeju.com": "최신 뉴스",
  "techoggi.com": "Ultime Notizie",
  "techhoy.com": "Últimas Noticias",
  "technikpost.de": "Neueste Nachrichten",
};

export function getHomeCategoryLabel(domain: string): string {
  const key = Object.keys(HOME_LABELS_BY_DOMAIN).find((k) => domain.includes(k));
  return key ? HOME_LABELS_BY_DOMAIN[key] : HOME_CATEGORY_LABEL;
}

/** Standard technology newsroom category set, shared by the tech tenants. */
const TECH_CATEGORIES = [
  "Artificial Intelligence",
  "Software Development",
  "Consumer Tech",
  "Cybersecurity",
  "Startups",
  "Big Tech",
  "Tech Policy",
  "Emerging Tech",
  "Digital Culture",
  "Enterprise & Cloud Infrastructure",
];

/** Same tech newsroom taxonomy as TECH_CATEGORIES, in Italian — TechOggi is an
 *  Italian-language site ("Tech Oggi" = "Tech Today"). Exported so TechOggi's
 *  keyword classifier (src/components/sites/techoggi/categorize.ts) can
 *  derive its nav/grouping categories straight from this canonical label set,
 *  since MediaStack's own `category` tag is always "general" for Italian
 *  results and can't be used for real categorization. */
export const TECH_CATEGORIES_IT = [
  "Intelligenza Artificiale",
  "Sviluppo Software",
  "Tecnologia di Consumo",
  "Sicurezza Informatica",
  "Startup",
  "Grandi Aziende Tech",
  "Politiche Tecnologiche",
  "Tecnologie Emergenti",
  "Cultura Digitale",
  "Impresa e Infrastruttura Cloud",
];

/** Same tech newsroom taxonomy as TECH_CATEGORIES, in Spanish — TechHoy is a
 *  Spanish-language site. */
const TECH_CATEGORIES_ES = [
  "Inteligencia Artificial",
  "Desarrollo de Software",
  "Tecnología de Consumo",
  "Ciberseguridad",
  "Startups",
  "Grandes Tecnológicas",
  "Política Tecnológica",
  "Tecnologías Emergentes",
  "Cultura Digital",
  "Empresa e Infraestructura Cloud",
];

export const TENANT_CATEGORIES: Record<string, string[]> = {
  "jejutime.com": [
    "Jeju Today",
    "Travel & Tourism",
    "Food & Restaurants",
    "Shopping & Markets",
    "Stay & Accommodation",
    "Getting Around",
    "Events & Festivals",
    "Nature & Outdoors",
    "Visa & Entry Info",
    "Local Business & Living",
  ],
  "jejuqq.com": [
    "济州今日",
    "旅游资讯",
    "美食餐厅",
    "购物市场",
    "住宿信息",
    "交通出行",
    "活动节庆",
    "自然户外",
    "签证入境",
    "本地商业生活",
  ],
  "jejujapan.com": [
    "済州今日",
    "旅行情報",
    "グルメ情報",
    "ショッピング",
    "宿泊情報",
    "交通・移動",
    "イベント・祭り",
    "自然・アウトドア",
    "ビザ・入国情報",
    "ローカルビジネス・生活",
  ],
  "lavaguetech.com": [
    "Artificial Intelligence",
    "Software Development",
    "Consumer Tech",
    "Cybersecurity",
    "Startups",
    "Big Tech",
    "Tech Policy",
    "Emerging Tech",
    "Digital Culture",
    "Enterprise & Cloud Infrastructure",
  ],
  "newsicons.com": [
    "Artificial Intelligence",
    "Software Development",
    "Consumer Tech",
    "Cybersecurity",
    "Startups",
    "Big Tech",
    "Tech Policy",
    "Emerging Tech",
    "Digital Culture",
    "Enterprise & Cloud Infrastructure",
  ],
  "skyblueprime.com": [
    "Artificial Intelligence",
    "Software Development",
    "Consumer Tech",
    "Cybersecurity",
    "Startups",
    "Big Tech",
    "Tech Policy",
    "Emerging Tech ",
    "Digital Culture",
    "Enterprise & Cloud Infrastructure",
  ],
  "voicejeju.com": [
    "제주 오늘",
    "여행 및 관광",
    "음식 및 맛집",
    "쇼핑 및 시장",
    "숙박 정보",
    "교통 및 이동",
    "이벤트 및 축제",
    "자연 및 아웃도어",
    "비자 및 입국 정보",
    "로컬 비즈니스 및 생활"
  ],
  "legalhyper.com": [
    "Legal AI",
    "LegalTech News",
    "Regulation & Policy",
    "Courts & Litigation",
    "Legal Geek Coverage",
    "Industry Analysis",
  ],
  // technews tenant family — shared standard tech category set
  "linktechnews.com": TECH_CATEGORIES,
  "dbtechnews.com": TECH_CATEGORIES,
  "magazinetechy.com": TECH_CATEGORIES,
  "magazineair.com": TECH_CATEGORIES,
  "techygate.com": TECH_CATEGORIES,
  "newyorksignal.com": TECH_CATEGORIES,
  "technikpost.de": TECH_CATEGORIES,
  "techoggi.com": TECH_CATEGORIES_IT,
  "techhoy.com": TECH_CATEGORIES_ES,
};

export const CATEGORY_TRANSLATIONS: Record<string, string> = {
  // Korean (VoiceJeju)
  "제주 오늘": "Jeju Today",
  "여행 및 관광": "Travel & Tourism",
  "음식 및 맛집": "Food & Restaurants",
  "쇼핑 및 시장": "Shopping & Markets",
  "숙박 정보": "Stay & Accommodation",
  "교통 및 이동": "Getting Around",
  "이벤트 및 축제": "Events & Festivals",
  "자연 및 아웃도어": "Nature & Outdoors",
  "비자 및 입국 정보": "Visa & Entry Info",
  "로컬 비즈니스 및 생활": "Local Business & Living",

  // Chinese (JejuQQ)
  "济州今日": "Jeju Today",
  "旅游资讯": "Travel & Tourism",
  "美食餐厅": "Food & Restaurants",
  "购物市场": "Shopping & Markets",
  "住宿信息": "Stay & Accommodation",
  "交通出行": "Getting Around",
  "活动节庆": "Events & Festivals",
  "自然户外": "Nature & Outdoors",
  "签证入境": "Visa & Entry Info",
  "本地商业生活": "Local Business & Living",

  // Japanese (JejuJapan)
  "済州今日": "Jeju Today",
  "旅行情報": "Travel & Tourism",
  "グルメ情報": "Food & Restaurants",
  "ショッピング": "Shopping & Markets",
  "宿泊情報": "Stay & Accommodation",
  "交通・移動": "Getting Around",
  "イベント・祭り": "Events & Festivals",
  "自然・アウトドア": "Nature & Outdoors",
  "ビザ・入国情報": "Visa & Entry Info",
  "ローカルビジネス・生活": "Local Business & Living",

  // Italian (TechOggi)
  "Intelligenza Artificiale": "Artificial Intelligence",
  "Sviluppo Software": "Software Development",
  "Tecnologia di Consumo": "Consumer Tech",
  "Sicurezza Informatica": "Cybersecurity",
  "Startup": "Startups",
  "Grandi Aziende Tech": "Big Tech",
  "Politiche Tecnologiche": "Tech Policy",
  "Tecnologie Emergenti": "Emerging Tech",
  "Cultura Digitale": "Digital Culture",
  "Impresa e Infrastruttura Cloud": "Enterprise & Cloud Infrastructure",
};

export function getCategoryLabel(categoryName: string): string {
  const translation = CATEGORY_TRANSLATIONS[categoryName];
  if (translation && translation !== categoryName) {
    return `${categoryName} (${translation})`;
  }
  return categoryName;
}

export function getCoreCategories(domain: string): string[] {
  // Normalize domain to check against keys
  const key = Object.keys(TENANT_CATEGORIES).find(k => domain.includes(k)) || "newsicons.com";
  return TENANT_CATEGORIES[key as keyof typeof TENANT_CATEGORIES];
}

// Keep legacy export for compatibility during transition
export const CORE_CATEGORIES = TENANT_CATEGORIES["newsicons.com"];

const CATEGORY_ALIASES: Record<string, string> = {
  "world news": "world",
  worldnews: "world",
  international: "world",
  "international news": "world",
  sport: "sports",
  "sports fitness": "sports",
  "sports and fitness": "sports",
  "latest stories": "latest news",
  "latest news": "latest news",
  "artificial intelligence and machine learning": "artificial intelligence",
  "ai and machine learning": "artificial intelligence",
  "ai machine learning": "artificial intelligence",
  "artificial intelligence machine learning": "artificial intelligence",
};

const CATEGORY_VARIANTS: Record<string, string[]> = {
  world: ["World", "World News", "International", "International News"],
  sports: ["Sports", "Sport", "Sports & Fitness", "Sports and Fitness"],
  technology: ["Technology", "Tech", "Tech News"],
  entertainment: ["Entertainment", "Lifestyle", "Showbiz"],
  business: ["Business", "Economy", "Finance"],
  travel: ["Travel", "Tourism"],
  science: ["Science", "Research"],
  politics: ["Politics", "Political"],
  "artificial intelligence": [
    "Artificial Intelligence",
    "Artificial Intelligence & Machine Learning",
    "AI & Machine Learning",
    "AI and Machine Learning",
  ],
};

export function normalizeCategoryKey(value: string | null | undefined): string {
  const base = (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ");

  return CATEGORY_ALIASES[base] ?? base;
}

export function getCategoryFilterVariants(
  value: string | null | undefined
): string[] {
  const key = normalizeCategoryKey(value);
  const source = (value ?? "").trim();
  const variants = CATEGORY_VARIANTS[key] ?? [];

  return Array.from(new Set([source, ...variants].filter(Boolean)));
}
